import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateConnector } from "@/api/mutations/connectors";
import type { Connector, ConnectorAuth } from "@/types";
import { buildOAuthAuth, buildClientCredentialsAuth } from "./connectorAuthForm";

interface Props {
  connector: Connector;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EnvRow { name: string; secret: boolean; required: boolean }

// #231/#237/#249/#253 (UC-4 / UC-A): Katalog-Felder bearbeiten. Editierbar ist jede nicht-geheime
// Config INNERHALB des bestehenden Transports/Auth-kinds. NICHT editierbar (würde die Vault-
// Verbindung verwaisen / die Shape umkrempeln): Transport-*Familie* (remote↔local↔cli↔openapi),
// Auth-*kind* (none↔static↔oauth↔login) und die cli-authShape — dafür „trennen + neu anlegen".
export function UpdateConnectorDialog({ connector, open, onOpenChange }: Props) {
  const isLocal = connector.transport === "local";
  const isCli = connector.transport === "cli";
  const isOpenApi = connector.transport === "openapi";
  const isRemote = !isLocal && !isCli && !isOpenApi;
  const authKind = connector.auth.kind;
  const oauthCfg = connector.auth.kind === "oauth" ? connector.auth : null;
  const staticCfg = connector.auth.kind === "static" ? connector.auth : null;
  const loginCfg = connector.auth.kind === "login" ? connector.auth : null;
  // #336 (UC-1): client-credentials — nicht-geheime Config (Creds laufen über „Verbinden").
  const ccCfg = connector.auth.kind === "client-credentials" ? connector.auth : null;
  // cli: authShape bleibt fix; binding-Typ (ENV-Name vs. Datei-Pfad) hängt davon ab.
  const cliAuthShape = connector.cli?.authShape ?? "none";
  const cliNeedsBinding = cliAuthShape === "env" || cliAuthShape === "file" || cliAuthShape === "oauth";

  const [name, setName] = useState(connector.name);
  const [description, setDescription] = useState(connector.description);
  const [url, setUrl] = useState(connector.url);
  const [transport, setTransport] = useState(connector.transport);
  // oauth
  const [clientId, setClientId] = useState(oauthCfg?.clientId ?? "");
  const [scopes, setScopes] = useState((oauthCfg?.scopes ?? []).join(" "));
  const [authServerHint, setAuthServerHint] = useState(oauthCfg?.authServerHint ?? "");
  // #336 (UC-2): explizite Endpoints (Discovery-Bypass) — beide oder keiner.
  const [authorizationEndpoint, setAuthorizationEndpoint] = useState(oauthCfg?.authorizationEndpoint ?? "");
  const [tokenEndpoint, setTokenEndpoint] = useState(oauthCfg?.tokenEndpoint ?? "");
  // #336 (UC-1) client-credentials
  const [ccTokenUrl, setCcTokenUrl] = useState(ccCfg?.tokenUrl ?? "");
  const [ccScopes, setCcScopes] = useState((ccCfg?.scopes ?? []).join(" "));
  const [ccAuthStyle, setCcAuthStyle] = useState<string>(ccCfg?.authStyle ?? "basic");
  // static
  const [header, setHeader] = useState(staticCfg?.header ?? "Authorization");
  const [scheme, setScheme] = useState(staticCfg?.scheme ?? "Bearer");
  // #253 (UC-A) login
  const [loginTokenUrl, setLoginTokenUrl] = useState(loginCfg?.tokenUrl ?? "");
  const [loginTokenPath, setLoginTokenPath] = useState(loginCfg?.tokenPath ?? "access_token");
  const [loginBodyFormat, setLoginBodyFormat] = useState<string>(loginCfg?.bodyFormat ?? "json");
  const [loginUserField, setLoginUserField] = useState(loginCfg?.userField ?? "username");
  const [loginPassField, setLoginPassField] = useState(loginCfg?.passField ?? "password");
  const [loginInjectHeader, setLoginInjectHeader] = useState(loginCfg?.injectHeader ?? "Authorization");
  const [loginInjectScheme, setLoginInjectScheme] = useState(loginCfg?.injectScheme ?? "Bearer");
  const [loginTtl, setLoginTtl] = useState(loginCfg?.ttlSeconds ? String(loginCfg.ttlSeconds) : "");
  // openapi
  const [baseUrl, setBaseUrl] = useState(connector.openapi?.baseUrl ?? "");
  const [specUrl, setSpecUrl] = useState(connector.openapi?.specUrl ?? "");
  const [openapiMode, setOpenapiMode] = useState(connector.openapi?.mode ?? "dynamic");
  const [includeTags, setIncludeTags] = useState((connector.openapi?.includeTags ?? []).join(" "));
  // local + cli
  const [command, setCommand] = useState(connector.local?.command ?? "");
  const [args, setArgs] = useState((connector.local?.args ?? []).join(" "));
  const [bin, setBin] = useState(connector.cli?.bin ?? "");
  const [installCmd, setInstallCmd] = useState(connector.cli?.installCmd ?? "");
  const [binding, setBinding] = useState(connector.cli?.binding ?? "");
  // envRows: local.envSchema bzw. cli.extraEnv
  const [envRows, setEnvRows] = useState<EnvRow[]>(
    (connector.local?.envSchema ?? connector.cli?.extraEnv ?? []).map((s) => ({ name: s.name, secret: s.secret, required: s.required })),
  );
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateConnector();

  // #253 (UC-A): die Auth-Config-Union aus den Feldern bauen (für den UNVERÄNDERTEN kind).
  // kind-Wechsel ist hier nicht möglich (= trennen+neu) → wir bauen exakt den vorhandenen kind.
  function buildAuth(): ConnectorAuth | null {
    if (authKind === "static") {
      if (!header.trim()) { setError("API-Key: Header-Name ist erforderlich"); return null; }
      return { kind: "static", header: header.trim(), scheme: scheme.trim() || "Bearer" };
    }
    if (authKind === "oauth") {
      // #336 (UC-2): Zusammenbau + Endpoint-Paar-Prüfung in der geteilten reinen Funktion
      // (dieselbe wie im Create-Dialog — keine zweite Kopie, die driften kann).
      const r = buildOAuthAuth({ clientId, scopes, authServerHint, authorizationEndpoint, tokenEndpoint });
      if (!r.ok) { setError(r.error); return null; }
      return r.auth;
    }
    // #336 (UC-1): client-credentials — tokenUrl/scopes/authStyle sind editierbar, die
    // Credentials nicht (die liegen im Vault und laufen über „Verbinden").
    if (authKind === "client-credentials") {
      const r = buildClientCredentialsAuth({ tokenUrl: ccTokenUrl, scopes: ccScopes, authStyle: ccAuthStyle });
      if (!r.ok) { setError(r.error); return null; }
      return r.auth;
    }
    if (authKind === "login") {
      if (!loginTokenUrl.trim()) { setError("Login: Token-URL ist erforderlich"); return null; }
      if (!loginTokenPath.trim()) { setError("Login: Token-Pfad ist erforderlich"); return null; }
      const ttl = parseInt(loginTtl.trim(), 10);
      return {
        kind: "login",
        tokenUrl: loginTokenUrl.trim(),
        bodyFormat: loginBodyFormat === "form" ? "form" : "json",
        userField: loginUserField.trim() || "username",
        passField: loginPassField.trim() || "password",
        tokenPath: loginTokenPath.trim(),
        injectHeader: loginInjectHeader.trim() || "Authorization",
        injectScheme: loginInjectScheme.trim() || "Bearer",
        ttlSeconds: Number.isFinite(ttl) && ttl > 0 ? ttl : null,
      };
    }
    return { kind: "none" };
  }

  function validEnv(): boolean {
    for (const r of envRows) {
      if (r.name.trim() && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(r.name.trim())) {
        setError(`Ungültiger ENV-Name: "${r.name}"`);
        return false;
      }
    }
    return true;
  }

  function submit() {
    setError(null);
    if (!name.trim()) return setError("Name ist erforderlich");

    const patch: Parameters<typeof mutation.mutate>[0]["patch"] = {
      name: name.trim(),
      description: description.trim(),
    };

    if (isLocal) {
      if (!command.trim()) return setError("Lokal: command ist erforderlich");
      if (!validEnv()) return;
      patch.transport = "local";
      patch.local = {
        command: command.trim(),
        args: args.split(/\s+/).map((a) => a.trim()).filter(Boolean),
        envSchema: envRows.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), secret: r.secret, required: r.required })),
      };
    } else if (isCli) {
      if (!bin.trim()) return setError("CLI: bin ist erforderlich");
      if (cliNeedsBinding && !binding.trim()) return setError(`CLI authShape '${cliAuthShape}' braucht ein binding`);
      if (!validEnv()) return;
      patch.transport = "cli";
      patch.cli = {
        bin: bin.trim(),
        installCmd: installCmd.trim() || null,
        uninstallCmd: connector.cli?.uninstallCmd ?? null,
        authShape: cliAuthShape, // fix — Auth-Shape-Wechsel = trennen+neu
        binding: cliNeedsBinding ? binding.trim() : null,
        extraEnv: envRows.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), secret: r.secret, required: r.required })),
      };
      // cli mit oauth-Shape → oauth-Meta (clientId/scopes/hint) editierbar
      if (authKind === "oauth") {
        const a = buildAuth();
        if (!a) return;
        patch.auth = a;
      }
    } else if (isOpenApi) {
      if (!baseUrl.trim()) return setError("OpenAPI: Basis-URL ist erforderlich");
      if (!specUrl.trim()) return setError("OpenAPI: Spec-URL ist erforderlich");
      patch.transport = "openapi";
      patch.openapi = {
        baseUrl: baseUrl.trim(),
        specUrl: specUrl.trim(),
        mode: openapiMode,
        includeTags: includeTags.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean),
      };
      if (authKind === "static" || authKind === "login" || authKind === "client-credentials") {
        const a = buildAuth();
        if (!a) return;
        patch.auth = a;
      }
    } else {
      if (!url.trim()) return setError("URL ist erforderlich");
      patch.url = url.trim();
      patch.transport = transport;
      if (authKind === "static" || authKind === "oauth" || authKind === "client-credentials") {
        const a = buildAuth();
        if (!a) return;
        patch.auth = a;
      }
    }

    mutation.mutate(
      { id: connector.id, patch },
      {
        onSuccess: () => {
          toast.success("Connector aktualisiert");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Fehler";
          setError(msg);
          toast.error(`Speichern fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  const authLabel = {
    none: "kein Auth",
    static: "API-Key/Bearer",
    oauth: "OAuth",
    login: "Login (user/pw → JWT)",
    "client-credentials": "Client-Credentials (Service-Identität)",
  }[authKind];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-connector-dialog">
        <DialogHeader>
          <DialogTitle>Connector bearbeiten</DialogTitle>
          <DialogDescription>
            Auth-Art (<span className="font-medium">{authLabel}</span>) + Transport sind fix — Wechsel = trennen + neu anlegen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="upd-name">Name *</Label>
            <Input id="upd-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="upd-desc">Beschreibung</Label>
            <Input id="upd-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {isLocal && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="upd-command">Command *</Label>
                <Input id="upd-command" value={command} onChange={(e) => setCommand(e.target.value)} placeholder="npx · uvx · /usr/local/bin/mcp-server" className="mono text-[12px]" data-testid="upd-field-command" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upd-args">Args <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                <Input id="upd-args" value={args} onChange={(e) => setArgs(e.target.value)} placeholder="-y @scope/server@1.2.3" className="mono text-[12px]" data-testid="upd-field-args" />
              </div>
              <EnvEditor envRows={envRows} setEnvRows={setEnvRows} defaultSecret />
            </div>
          )}

          {isCli && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="upd-bin">bin *</Label>
                <Input id="upd-bin" value={bin} onChange={(e) => setBin(e.target.value)} placeholder="gws · gh · aws" className="mono text-[12px]" data-testid="upd-field-bin" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upd-installcmd">Install-Befehl <span className="text-text3">(optional)</span></Label>
                <Input id="upd-installcmd" value={installCmd} onChange={(e) => setInstallCmd(e.target.value)} placeholder="npm i -g @google/gws-cli" className="mono text-[12px]" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Auth-Shape <span className="text-text3">(fix)</span></Label>
                  <Input value={cliAuthShape} disabled className="mono text-[12px]" />
                </div>
                {cliNeedsBinding && (
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="upd-binding">{cliAuthShape === "file" ? "Datei-Pfad *" : "ENV-Variable *"}</Label>
                    <Input id="upd-binding" value={binding} onChange={(e) => setBinding(e.target.value)} className="mono text-[12px]" data-testid="upd-field-binding" />
                  </div>
                )}
              </div>
              <EnvEditor envRows={envRows} setEnvRows={setEnvRows} defaultSecret={false} label="Zusätzliche ENV" />
            </div>
          )}

          {isOpenApi && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="upd-baseurl">API-Basis-URL *</Label>
                <Input id="upd-baseurl" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com/v2" className="mono text-[12px]" data-testid="upd-field-baseurl" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upd-specurl">OpenAPI-/Swagger-Spec-URL *</Label>
                <Input id="upd-specurl" value={specUrl} onChange={(e) => setSpecUrl(e.target.value)} placeholder="https://api.example.com/v2/swagger.json" className="mono text-[12px]" data-testid="upd-field-specurl" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label>Tool-Modus</Label>
                  <Select value={openapiMode} onValueChange={setOpenapiMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dynamic">dynamic (3 Meta-Tools)</SelectItem>
                      <SelectItem value="all">all (ein Tool je Operation)</SelectItem>
                      <SelectItem value="explicit">explicit (nur via Tags)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-tags">Tags <span className="text-text3">(optional)</span></Label>
                  <Input id="upd-tags" value={includeTags} onChange={(e) => setIncludeTags(e.target.value)} placeholder="pet store" className="mono text-[12px]" data-testid="upd-field-includetags" />
                </div>
              </div>
            </div>
          )}

          {isRemote && (
            <>
              <div className="space-y-1">
                <Label htmlFor="upd-url">MCP-URL *</Label>
                <Input id="upd-url" value={url} onChange={(e) => setUrl(e.target.value)} className="mono text-[12px]" />
              </div>
              <div className="space-y-1">
                <Label>Transport</Label>
                <Select value={transport} onValueChange={setTransport}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">auto</SelectItem>
                    <SelectItem value="streamable-http">streamable-http</SelectItem>
                    <SelectItem value="sse">sse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* static (API-Key/Bearer) — Header + Schema */}
          {authKind === "static" && (
            <div className="flex gap-3 rounded-md border border-border bg-surface2 p-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="upd-header">Header *</Label>
                <Input id="upd-header" value={header} onChange={(e) => setHeader(e.target.value)} placeholder="Authorization" className="mono text-[12px]" data-testid="upd-field-header" />
              </div>
              <div className="w-40 space-y-1">
                <Label htmlFor="upd-scheme">Schema</Label>
                <Input id="upd-scheme" value={scheme} onChange={(e) => setScheme(e.target.value)} placeholder="Bearer" />
              </div>
            </div>
          )}

          {/* oauth — clientId/scopes/authServerHint (remote + cli-oauth) */}
          {authKind === "oauth" && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="upd-clientid">OAuth Client-ID <span className="text-text3">(leer = DCR)</span></Label>
                <Input id="upd-clientid" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="leer lassen für Dynamic Client Registration" className="mono text-[12px]" data-testid="upd-field-clientid" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upd-scopes">Scopes <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                <Input id="upd-scopes" value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="default" className="mono text-[12px]" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upd-ash">Auth-Server-Hint <span className="text-text3">(optional)</span></Label>
                <Input id="upd-ash" value={authServerHint} onChange={(e) => setAuthServerHint(e.target.value)} placeholder="https://app.asana.com" className="mono text-[12px]" />
              </div>
              {/* #336 (UC-2): explizite Endpoints — für Anbieter ohne RFC-8414-Metadata am Host-Root. */}
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-authep">Authorize-Endpoint <span className="text-text3">(optional)</span></Label>
                  <Input id="upd-authep" value={authorizationEndpoint} onChange={(e) => setAuthorizationEndpoint(e.target.value)} placeholder="https://host/oauth/oauth2/v1/auth" className="mono text-[12px]" data-testid="upd-field-authep" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-tokenep">Token-Endpoint <span className="text-text3">(optional)</span></Label>
                  <Input id="upd-tokenep" value={tokenEndpoint} onChange={(e) => setTokenEndpoint(e.target.value)} placeholder="https://host/oauth/oauth2/v1/token" className="mono text-[12px]" data-testid="upd-field-tokenep" />
                </div>
              </div>
              <p className="text-[11px] text-text3">Beide gesetzt = keine Discovery und kein DCR (Client-ID wird Pflicht). Beide leer = Discovery wie bisher.</p>
            </div>
          )}

          {/* #336 (UC-1) client-credentials — tokenUrl/scopes/authStyle */}
          {authKind === "client-credentials" && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3" data-testid="upd-cc-block">
              <div className="space-y-1">
                <Label htmlFor="upd-cc-url">Token-URL *</Label>
                <Input id="upd-cc-url" value={ccTokenUrl} onChange={(e) => setCcTokenUrl(e.target.value)} placeholder="https://host/oauth/oauth2/v1/token" className="mono text-[12px]" data-testid="upd-field-cc-url" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-cc-scopes">Scopes <span className="text-text3">(Leerzeichen-getrennt)</span></Label>
                  <Input id="upd-cc-scopes" value={ccScopes} onChange={(e) => setCcScopes(e.target.value)} placeholder="API" className="mono text-[12px]" data-testid="upd-field-cc-scopes" />
                </div>
                <div className="w-56 space-y-1">
                  <Label>Client-Auth</Label>
                  <Select value={ccAuthStyle} onValueChange={setCcAuthStyle}>
                    <SelectTrigger data-testid="upd-field-cc-authstyle"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">basic (Authorization-Header)</SelectItem>
                      <SelectItem value="body">body (client_id/secret im Body)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-text3">Client-ID + Client-Secret änderst du über „Verbinden", nicht hier — hier nur die Endpoint-Config.</p>
            </div>
          )}

          {/* #253 (UC-A) login — tokenUrl/tokenPath/Felder/Inject/TTL */}
          {authKind === "login" && (
            <div className="space-y-3 rounded-md border border-border bg-surface2 p-3">
              <div className="space-y-1">
                <Label htmlFor="upd-login-url">Login-Token-URL *</Label>
                <Input id="upd-login-url" value={loginTokenUrl} onChange={(e) => setLoginTokenUrl(e.target.value)} placeholder="https://api.example.com/auth/login" className="mono text-[12px]" data-testid="upd-field-login-url" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-login-path">Token-Pfad *</Label>
                  <Input id="upd-login-path" value={loginTokenPath} onChange={(e) => setLoginTokenPath(e.target.value)} placeholder="access_token · data.token" className="mono text-[12px]" data-testid="upd-field-login-path" />
                </div>
                <div className="w-32 space-y-1">
                  <Label>Body-Format</Label>
                  <Select value={loginBodyFormat} onValueChange={setLoginBodyFormat}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="form">Form</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-login-userfield">User-Feld</Label>
                  <Input id="upd-login-userfield" value={loginUserField} onChange={(e) => setLoginUserField(e.target.value)} placeholder="username" className="mono text-[12px]" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-login-passfield">Passwort-Feld</Label>
                  <Input id="upd-login-passfield" value={loginPassField} onChange={(e) => setLoginPassField(e.target.value)} placeholder="password" className="mono text-[12px]" />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="upd-login-injheader">Inject-Header</Label>
                  <Input id="upd-login-injheader" value={loginInjectHeader} onChange={(e) => setLoginInjectHeader(e.target.value)} placeholder="Authorization" className="mono text-[12px]" />
                </div>
                <div className="w-28 space-y-1">
                  <Label htmlFor="upd-login-injscheme">Scheme</Label>
                  <Input id="upd-login-injscheme" value={loginInjectScheme} onChange={(e) => setLoginInjectScheme(e.target.value)} placeholder="Bearer" className="mono text-[12px]" />
                </div>
                <div className="w-28 space-y-1">
                  <Label htmlFor="upd-login-ttl">TTL (s)</Label>
                  <Input id="upd-login-ttl" value={loginTtl} onChange={(e) => setLoginTtl(e.target.value)} placeholder="auto" className="mono text-[12px]" />
                </div>
              </div>
              <p className="text-[11px] text-text3">Credentials (user/pw) änderst du über „Verbinden", nicht hier — hier nur die Endpoint-Config.</p>
            </div>
          )}

          {error && <div className="text-xs text-danger" data-testid="upd-error">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={mutation.isPending}>{mutation.isPending ? "…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Wiederverwendbarer ENV-Zeilen-Editor (local.envSchema / cli.extraEnv).
function EnvEditor({
  envRows, setEnvRows, defaultSecret, label = "ENV-Variablen",
}: {
  envRows: EnvRow[];
  setEnvRows: React.Dispatch<React.SetStateAction<EnvRow[]>>;
  defaultSecret: boolean;
  label?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label>{label} <span className="text-text3">(Werte beim Verbinden)</span></Label>
        <Button type="button" variant="outline" size="sm" onClick={() => setEnvRows((r) => [...r, { name: "", secret: defaultSecret, required: defaultSecret }])}>+ Variable</Button>
      </div>
      {envRows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={row.name} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))} placeholder="GITHUB_TOKEN" className="mono text-[12px] flex-1" />
          <label className="flex items-center gap-1 text-[11px] text-text2"><input type="checkbox" checked={row.secret} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, secret: e.target.checked } : r)))} /> secret</label>
          <label className="flex items-center gap-1 text-[11px] text-text2"><input type="checkbox" checked={row.required} onChange={(e) => setEnvRows((rows) => rows.map((r, j) => (j === i ? { ...r, required: e.target.checked } : r)))} /> required</label>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEnvRows((rows) => rows.filter((_, j) => j !== i))}>✕</Button>
        </div>
      ))}
    </div>
  );
}
